/**
 * Typed customer API client (SPEC §6a/§6d/§7). Every call attaches the Entra
 * bearer token via the injected token getter (see useApi), mirroring useUserRole.
 */
import axios from 'axios';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:7071/api';

export interface CustomerListItem {
  customer_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  owning_store_id: string;
  store_code: string;
  store_name: string;
  /** Provisioned from / linked to the BC wholesale scheme. */
  is_wholesale: boolean;
  /** Linked to a Shopify customer — has a web presence. */
  has_web: boolean;
  /** In-store activity (email-folded across the person's identities); list only. */
  last_visit?: string | null;
  txns_12m?: number;
  spend_12m?: number;
  spend_total?: number;
}

/** List filter: wholesale members vs customers added in store. */
export type CustomerSegment = 'wholesale' | 'store_added';

/** List sort orders. */
export type CustomerSort = 'name' | 'newest' | 'recent' | 'spend12_desc' | 'spend12_asc' | 'total_desc';

export interface CustomerListOptions {
  segment?: CustomerSegment;
  sort?: CustomerSort;
  /** Office users: restrict the list to one store by code. */
  store?: string;
}

export type ShopifySyncStatus = 'pending' | 'synced' | 'retry';

export interface CustomerRecord extends CustomerListItem {
  bc_customer_id: string | null;
  shopify_customer_gid: string | null;
  klaviyo_profile_id: string | null;
  shopify_sync_status: ShopifySyncStatus;
  created_at: string;
  updated_at: string;
}

/** BC loyalty card (read-only, §6a). */
export interface BCMemberCard {
  memberId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  tier: string;
  points: number;
  joinedAt: string;
}

export interface BCSaleLine { sku: string; productName: string; quantity: number; unitPrice: number; }
export interface BCSale { saleId: string; date: string; store: string; total: number; lines: BCSaleLine[]; }

/** BC contact match surfaced in dedupe (§6d). */
export interface BCMemberContact {
  memberId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

export interface ShopifyOrderLine { sku: string; title: string; quantity: number; }
export interface ShopifyOrder {
  id: string;
  name: string;
  processedAt: string;
  totalPrice: number;
  fulfillmentStatus: string;
  lineItems: ShopifyOrderLine[];
}

/** Shopify customer match surfaced in dedupe (§6d). */
export interface ShopifyCustomerMatch {
  gid: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  emailMarketingConsent: boolean;
  smsMarketingConsent: boolean;
}

/** GET /api/customers/{id} — engagement record + live reads (unlinked → null). */
export interface Favourite {
  favourite_id: string;
  sku: string;
  product_name: string;
  note: string | null;
  added_at: string;
}

export interface KeyDate {
  key_date_id: string;
  type: 'birthday' | 'anniversary' | 'custom';
  label: string | null;
  month: number;
  day: number;
  year: number | null;
  recurs: boolean;
  source: string | null;
}

export interface CustomerNote {
  note_id: string;
  body: string;
  author_name: string | null;
  created_at: string;
}

export interface CustomerActivity {
  last_visit: string | null;
  txns_12m: number;
  spend_12m: number;
  spend_total: number;
}

export interface TransactionLine {
  line_no: number;
  item_no: string | null;
  description: string | null;
  quantity: number | null;
  price_paid: number | null;
  discount_amount: number | null;
}

export interface CustomerTransaction {
  store_no: string;
  pos_terminal_no: string;
  transaction_no: number;
  receipt_no: string | null;
  txn_date: string;
  txn_time: string | null;
  amount: number;
  quantity: number | null;
  refunded: boolean;
  store_name: string | null;
  /** Which identity was swiped: the wholesale card or the personal (app) card. */
  channel: 'wholesale' | 'app';
  lines: TransactionLine[];
  note: CustomerNote | null;
}

export interface TransactionPage {
  results: CustomerTransaction[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface CustomerDetail {
  customer: CustomerRecord;
  interests: string[];
  favourites: Favourite[];
  keyDates: KeyDate[];
  notes: CustomerNote[];
  activity: CustomerActivity | null;
}

export interface ProductHit {
  item_no: string;
  description: string;
  item_category_code: string | null;
}

export interface HomeSummary {
  outreachToday: number;
  keyDatesThisWeek: number;
  overdue: number;
  promoMatches: number;
  nextPromotion: { title: string; startsOn: string; endsOn: string };
}

export async function getHomeSummary(getHeaders: HeadersGetter): Promise<HomeSummary> {
  const headers = await getHeaders();
  const res = await axios.get<HomeSummary>(`${API_BASE}/home/summary`, { headers });
  return res.data;
}

export async function searchProducts(getHeaders: HeadersGetter, q: string, signal?: AbortSignal): Promise<ProductHit[]> {
  const headers = await getHeaders();
  const res = await axios.get<{ results: ProductHit[] }>(`${API_BASE}/products/search`, { params: { q }, signal, headers });
  return res.data.results;
}

export async function addFavourite(getHeaders: HeadersGetter, id: string, fav: { sku: string; productName: string; note?: string }): Promise<Favourite[]> {
  const headers = await getHeaders();
  const res = await axios.post<{ favourites: Favourite[] }>(`${API_BASE}/customers/${id}/favourites`, fav, { headers });
  return res.data.favourites;
}

export async function removeFavourite(getHeaders: HeadersGetter, id: string, favId: string): Promise<Favourite[]> {
  const headers = await getHeaders();
  const res = await axios.delete<{ favourites: Favourite[] }>(`${API_BASE}/customers/${id}/favourites/${favId}`, { headers });
  return res.data.favourites;
}

export async function addKeyDate(getHeaders: HeadersGetter, id: string, kd: { type: string; label?: string; month: number; day: number; year?: number; source?: string }): Promise<KeyDate[]> {
  const headers = await getHeaders();
  const res = await axios.post<{ keyDates: KeyDate[] }>(`${API_BASE}/customers/${id}/key-dates`, kd, { headers });
  return res.data.keyDates;
}

export async function removeKeyDate(getHeaders: HeadersGetter, id: string, kdId: string): Promise<KeyDate[]> {
  const headers = await getHeaders();
  const res = await axios.delete<{ keyDates: KeyDate[] }>(`${API_BASE}/customers/${id}/key-dates/${kdId}`, { headers });
  return res.data.keyDates;
}

/** 409 body when a likely duplicate is found (SPEC §6d). */
export interface DuplicateCandidates {
  error: 'possible_duplicate';
  message: string;
  candidates: {
    local: CustomerListItem[];
    shopify: ShopifyCustomerMatch[];
    bc: BCMemberContact[];
  };
}

export interface CreateCustomerInput {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  homeStoreId?: string;
  optIn?: boolean;
  createAnyway?: boolean;
  /** Adopt an existing identity: creates the local record linked to it and
   *  skips the Shopify write (it already exists). */
  link?: { shopifyGid?: string; bcContactNo?: string };
}

/** A person in the mirrors (Shopify / BC) not yet in the caller's list. */
export interface ExternalMatch {
  source: 'shopify' | 'bc';
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  shopify_gid: string | null;
  bc_contact_no: string | null;
}

/** Returns the auth headers for a request — the Entra bearer token. */
export type HeadersGetter = () => Promise<Record<string, string>>;

export interface CustomerListPage {
  results: CustomerListItem[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

/** Paginated list of the caller's customers (store-scoped by RLS). */
export async function listCustomers(
  getHeaders: HeadersGetter,
  limit: number,
  offset: number,
  signal?: AbortSignal,
  opts: CustomerListOptions = {}
): Promise<CustomerListPage> {
  const headers = await getHeaders();
  const res = await axios.get<CustomerListPage>(`${API_BASE}/customers`, {
    params: {
      limit,
      offset,
      ...(opts.segment ? { segment: opts.segment } : {}),
      ...(opts.sort ? { sort: opts.sort } : {}),
      ...(opts.store ? { store: opts.store } : {}),
    },
    signal,
    headers,
  });
  return res.data;
}

export interface StoreListItem {
  store_id: string;
  code: string;
  name: string;
}

/** GET /api/stores — active store list for filter dropdowns. */
export async function listStores(getHeaders: HeadersGetter): Promise<StoreListItem[]> {
  const headers = await getHeaders();
  const res = await axios.get<{ results: StoreListItem[] }>(`${API_BASE}/stores`, { headers });
  return res.data.results;
}

export async function searchCustomers(
  getHeaders: HeadersGetter,
  q: string,
  signal?: AbortSignal
): Promise<{ results: CustomerListItem[]; external: ExternalMatch[] }> {
  const headers = await getHeaders();
  const res = await axios.get<{ results: CustomerListItem[]; external?: ExternalMatch[] }>(
    `${API_BASE}/customers/search`,
    { params: { q }, signal, headers }
  );
  return { results: res.data.results, external: res.data.external ?? [] };
}

export async function getCustomer(
  getHeaders: HeadersGetter,
  id: string
): Promise<CustomerDetail> {
  const headers = await getHeaders();
  const res = await axios.get<CustomerDetail>(`${API_BASE}/customers/${id}`, { headers });
  return res.data;
}

export async function updateCustomer(
  getHeaders: HeadersGetter,
  id: string,
  patch: { firstName?: string; lastName?: string; phone?: string; email?: string }
): Promise<{ customer: CustomerRecord }> {
  const headers = await getHeaders();
  const res = await axios.put<{ customer: CustomerRecord }>(`${API_BASE}/customers/${id}`, patch, { headers });
  return res.data;
}

/** Create + link the Shopify identity for a standalone customer. */
export async function createShopifyIdentity(
  getHeaders: HeadersGetter,
  id: string
): Promise<{ customer: CustomerRecord }> {
  const headers = await getHeaders();
  const res = await axios.post<{ customer: CustomerRecord }>(`${API_BASE}/customers/${id}/shopify`, {}, { headers });
  return res.data;
}

/** Paged in-store receipts (items + per-receipt note) from the local mirror. */
export async function getTransactions(
  getHeaders: HeadersGetter,
  id: string,
  limit: number,
  offset: number
): Promise<TransactionPage> {
  const headers = await getHeaders();
  const res = await axios.get<TransactionPage>(`${API_BASE}/customers/${id}/transactions`, {
    params: { limit, offset },
    headers,
  });
  return res.data;
}

/** Add a note — customer-level, or per-receipt when txn is given. Guest-only. */
export async function addNote(
  getHeaders: HeadersGetter,
  id: string,
  body: string,
  txn?: { storeNo: string; posTerminalNo: string; transactionNo: number }
): Promise<CustomerNote[]> {
  const headers = await getHeaders();
  const res = await axios.post<{ results: CustomerNote[] }>(
    `${API_BASE}/customers/${id}/notes`,
    { body, txn },
    { headers }
  );
  return res.data.results;
}

export interface CheckResult {
  hasMatch: boolean;
  candidates: DuplicateCandidates['candidates'];
  phoneE164: string | null;
}

/** Pre-capture dedupe check by email/phone — does not create anything. */
export async function checkDuplicate(
  getHeaders: HeadersGetter,
  email: string,
  phone: string
): Promise<CheckResult> {
  const headers = await getHeaders();
  const res = await axios.get<CheckResult>(`${API_BASE}/customers/check`, {
    params: { email, phone },
    headers,
  });
  return res.data;
}

/** Toggle a customer interest tag; returns the updated tag list. */
export async function setInterest(
  getHeaders: HeadersGetter,
  id: string,
  tag: string,
  on: boolean
): Promise<string[]> {
  const headers = await getHeaders();
  const res = await axios.put<{ interests: string[] }>(
    `${API_BASE}/customers/${id}/interests`,
    { tag, on },
    { headers }
  );
  return res.data.interests;
}

export interface CreateResult {
  ok: boolean;
  customer?: CustomerRecord;
  duplicate?: DuplicateCandidates;
}

/** Create; a 409 is returned as { ok:false, duplicate } rather than thrown. */
export async function createCustomer(
  getHeaders: HeadersGetter,
  input: CreateCustomerInput
): Promise<CreateResult> {
  const headers = await getHeaders();
  try {
    const res = await axios.post<{ customer: CustomerRecord }>(
      `${API_BASE}/customers`,
      input,
      { headers }
    );
    return { ok: true, customer: res.data.customer };
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 409) {
      return { ok: false, duplicate: err.response.data as DuplicateCandidates };
    }
    throw err;
  }
}
